import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  authApi,
  usersApi,
  setAccessToken,
  setRefreshToken,
  getRefreshToken,
  clearTokens,
} from "@/lib/api";
import type { User } from "@/lib/types";

interface AuthContextType {
  user: User | null;
  teamId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    captcha?: { ticket: string; randstr: string },
  ) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Bootstrap: try to restore session from refresh token
  useEffect(() => {
    const bootstrap = async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        setIsLoading(false);
        return;
      }

      try {
        const resp = await authApi.refresh(refreshToken);
        setAccessToken(resp.access_token);
        setRefreshToken(resp.refresh_token);

        // Fetch full user profile
        const profile = await usersApi.getMe();
        setUser(profile);
      } catch {
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    bootstrap();
  }, []);

  const login = useCallback(
    async (
      email: string,
      password: string,
      captcha?: { ticket: string; randstr: string },
    ) => {
      const resp = await authApi.login(email, password, captcha);
      setAccessToken(resp.access_token);
      setRefreshToken(resp.refresh_token);
      setUser(resp.user);
      return resp.user;
    },
    [],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    clearTokens();
    setUser(null);
    window.location.href = "/login";
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const profile = await usersApi.getMe();
      setUser(profile);
    } catch {
      // ignore
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        teamId: user?.team_id || null,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
